import { 
  DocumentIcon, 
  ChatBubbleLeftIcon, 
  ClockIcon,
  PaperClipIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlayIcon,
  EyeIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'

interface SearchResult {
  type: 'issue' | 'activity' | 'comment' | 'attachment' | 'worklog'
  id: string
  title: string
  description: string
  projectKey: string
  projectName: string
  user: {
    id: string
    name: string
    email: string
    avatar: string | null
  }
  createdAt: string
  relevanceScore: number
  metadata: {
    issueKey?: string
    issueStatus?: string
    activityType?: string
    issueTitle?: string
    issueId?: string
    issueDeleted?: boolean
    issueArchived?: boolean
    field?: string
    oldValue?: string
    newValue?: string
    originalName?: string
    fileSize?: number
    mimeType?: string
    filepath?: string
    parentIssue?: string
    workLogType?: 'project' | 'global'
    workLogStatus?: string
    workLogDateRange?: string
  }
}

interface SearchResultsProps {
  results: SearchResult[]
  loading: boolean
  pagination: {
    page: number
    limit: number
    totalCount: number
    totalPages: number
  }
  query: string
  onPageChange: (page: number) => void
  pageSize: number
  onPageSizeChange: (pageSize: number) => void
}

const SearchResults = ({ results, loading, pagination, query, onPageChange, pageSize, onPageSizeChange }: SearchResultsProps) => {
  const navigate = useNavigate()
  
  // 智能內容提取函數，保留檔案資訊
  const smartContentExtract = (html: string): string => {
    if (!html) return '';
    
    // 先清理和正規化HTML
    const cleanHtml = html.trim();
    if (!cleanHtml) return '';
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanHtml;
    
    // 處理圖片標籤，保留檔案名稱
    const images = tempDiv.querySelectorAll<HTMLImageElement>('img');
    images.forEach((img) => {
      const filename = img.alt || img.title || 
                      (img.src ? img.src.split('/').pop()?.split('?')[0] : null) || 
                      '圖片';
      const placeholder = document.createTextNode(`[圖片: ${filename}]`);
      img.parentNode?.replaceChild(placeholder, img);
    });
    
    // 處理檔案連結，保留檔案名稱
    const links = tempDiv.querySelectorAll<HTMLAnchorElement>('a[href]');
    links.forEach((link) => {
      const filename = link.getAttribute('download') || 
                      link.textContent?.trim() || 
                      (link.href ? link.href.split('/').pop()?.split('?')[0] : null) || 
                      '檔案';
      const placeholder = document.createTextNode(`[檔案: ${filename}]`);
      link.parentNode?.replaceChild(placeholder, link);
    });
    
    // 提取純文字內容
    let textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // 清理多餘的空白字符和HTML實體
    textContent = textContent
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
    
    // 如果清理後沒有實質內容，返回預設文字
    return textContent || '(無文字內容)';
  };
  
  // 處理議題點擊
  const handleIssueClick = (issueId: string) => {
    navigate(`/issues/${issueId}`)
  }
  
  // 處理工作日誌點擊
  const handleWorkLogClick = (workLogId: string, workLogType?: 'project' | 'global') => {
    if (workLogType === 'global') {
      navigate(`/global-worklogs/${workLogId}`)
    } else {
      // 專案工作日誌暫時導向到議題頁面或專案頁面
      // 未來可以實作專門的工作日誌詳細頁面
      navigate(`/worklogs/${workLogId}`)
    }
  }
  
  // 截斷文字
  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }
  
  // 取得欄位標籤
  const getFieldLabel = (field: string) => {
    const fieldLabels: Record<string, string> = {
      'title': '標題',
      'description': '描述',
      'status': '狀態',
      'customStatus': '自訂狀態',
      'priority': '優先級',
      'assigneeId': '負責人'
    }
    return fieldLabels[field] || field
  }
  
  // 格式化檔案大小
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // 獲取結果圖示
  const getResultIcon = (result: SearchResult) => {
    switch (result.type) {
      case 'issue':
        return <DocumentIcon className="w-5 h-5 text-blue-600" />
      case 'activity':
        return <ClockIcon className="w-5 h-5 text-green-600" />
      case 'comment':
        return <ChatBubbleLeftIcon className="w-5 h-5 text-purple-600" />
      case 'attachment':
        return <PaperClipIcon className="w-5 h-5 text-orange-600" />
      case 'worklog':
        return <DocumentTextIcon className="w-5 h-5 text-indigo-600" />
      default:
        return <DocumentIcon className="w-5 h-5 text-gray-600" />
    }
  }

  // 獲取結果類型標籤
  const getResultTypeLabel = (type: string) => {
    const labels = {
      'issue': '議題',
      'activity': '活動',
      'comment': '評論',
      'attachment': '附件',
      'worklog': '工作日誌'
    }
    return labels[type as keyof typeof labels] || type
  }

  // 獲取狀態圖示
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'TODO':
        return <ExclamationTriangleIcon className="w-4 h-4 text-gray-500" />
      case 'IN_PROGRESS':
        return <PlayIcon className="w-4 h-4 text-blue-500" />
      case 'IN_REVIEW':
        return <EyeIcon className="w-4 h-4 text-yellow-500" />
      case 'DONE':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />
      default:
        return null
    }
  }

  // 獲取狀態標籤
  const getStatusLabel = (status: string) => {
    const labels = {
      'TODO': '待辦',
      'IN_PROGRESS': '進行中',
      'IN_REVIEW': '待審核',
      'DONE': '完成'
    }
    return labels[status as keyof typeof labels] || status
  }

  // 高亮搜尋關鍵字
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-medium">
          {part}
        </span>
      ) : part
    )
  }

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-5 h-5 bg-gray-300 rounded"></div>
                  <div className="h-4 bg-gray-300 rounded w-20"></div>
                  <div className="h-4 bg-gray-300 rounded w-32"></div>
                </div>
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="p-6 text-center">
          <div className="text-gray-400 mb-4">
            <DocumentIcon className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            沒有找到搜尋結果
          </h3>
          <p className="text-gray-500">
            嘗試調整搜尋關鍵字或篩選條件
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg">
      {/* 搜尋結果標題 */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            搜尋結果 
            <span className="text-sm text-gray-500 ml-2">
              ({pagination.totalCount} 個結果)
            </span>
          </h2>
          <div className="text-sm text-gray-500">
            第 {pagination.page} 頁，共 {pagination.totalPages} 頁
          </div>
        </div>
      </div>

      {/* 搜尋結果列表 */}
      <div className="divide-y divide-gray-200">
        {results.map((result, index) => {
          // 計算結果編號（考慮分頁）
          const resultNumber = (pagination.page - 1) * pagination.limit + index + 1
          
          return (
            <div key={`${result.type}-${result.id}`} className="p-6 hover:bg-gray-50">
              {/* 結果標頭 */}
              <div className="flex items-center space-x-3 mb-2">
                {/* 編號 */}
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-medium">
                  {resultNumber}
                </div>
                {getResultIcon(result)}
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {getResultTypeLabel(result.type)}
                </span>
                <span className="text-sm text-gray-500">
                  {result.projectName} ({result.projectKey})
                </span>
                
                {/* 議題相關資訊移到這裡 */}
                {result.metadata.issueKey && result.metadata.issueTitle && result.metadata.issueId && (
                  <>
                    <span className="text-sm text-gray-400">•</span>
                    <span className="text-sm text-gray-500">議題: </span>
                    <button
                      onClick={() => handleIssueClick(result.metadata.issueId!)}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors text-sm"
                    >
                      {result.metadata.issueTitle}({result.metadata.issueKey})
                    </button>
                  </>
                )}
                
                {result.metadata.issueStatus && (
                  <div className="flex items-center space-x-1">
                    {getStatusIcon(result.metadata.issueStatus)}
                    <span className="text-xs text-gray-500">
                      {getStatusLabel(result.metadata.issueStatus)}
                    </span>
                  </div>
                )}
                <div className="text-xs text-gray-400">
                  相關度: {result.relevanceScore}%
                </div>
              </div>

            {/* 結果標題與描述 */}
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {result.type === 'worklog' ? (
                <button
                  onClick={() => handleWorkLogClick(result.id, result.metadata.workLogType)}
                  className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  {highlightText(result.title, query)}
                </button>
              ) : (
                highlightText(result.title, query)
              )}
              {result.description && (
                <span className="text-gray-600 font-normal ml-2">
                  {highlightText(smartContentExtract(result.description), query)}
                </span>
              )}
            </h3>

            {/* 變更詳細記錄 */}
            {result.type === 'activity' && (
              result.metadata.activityType === 'UPDATED' || 
              result.metadata.activityType === 'STATUS_CHANGED' || 
              result.metadata.activityType === 'ASSIGNED' ||
              result.metadata.activityType === 'COMMENTED'
            ) && result.metadata.field && (result.metadata.oldValue !== undefined || result.metadata.newValue !== undefined) && (
              <div className="text-sm text-gray-600 mb-3 bg-gray-50 p-3 rounded-md">
                <div className="flex items-center mb-2">
                  <span className="text-gray-500 font-medium">變更詳情：</span>
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                    {result.metadata.field === 'comment' ? '留言' : getFieldLabel(result.metadata.field)}
                  </span>
                </div>
                
                {/* 新增留言的情況 */}
                {result.metadata.activityType === 'COMMENTED' && result.metadata.newValue && !result.metadata.oldValue ? (
                  <div className="p-2 bg-green-50 border-l-2 border-green-200 rounded text-sm">
                    <div className="text-xs text-gray-500 mb-1">留言內容：</div>
                    {truncateText(smartContentExtract(result.metadata.newValue), 150)}
                  </div>
                ) : (
                  /* 修改或刪除留言的情況 */
                  <div className="flex items-center space-x-3">
                    {result.metadata.oldValue && (
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">變更前：</div>
                        <div className="p-2 bg-red-50 border-l-2 border-red-200 rounded text-sm">
                          {truncateText(smartContentExtract(result.metadata.oldValue || '(空值)'), 100)}
                        </div>
                      </div>
                    )}
                    {result.metadata.oldValue && result.metadata.newValue && (
                      <div className="text-gray-400">→</div>
                    )}
                    {result.metadata.newValue && (
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">變更後：</div>
                        <div className="p-2 bg-green-50 border-l-2 border-green-200 rounded text-sm">
                          {truncateText(smartContentExtract(result.metadata.newValue || '(空值)'), 100)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 結果元數據 */}
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <img
                  src={result.user.avatar || `https://ui-avatars.com/api/?name=${result.user.name}&background=random`}
                  alt={result.user.name}
                  className="w-6 h-6 rounded-full"
                />
                <span>{result.user.name}</span>
              </div>
              
              <span>•</span>
              
              <span>
                {formatDistanceToNow(new Date(result.createdAt), { 
                  addSuffix: true, 
                  locale: zhTW 
                })}
              </span>

              {/* 檔案相關資訊 */}
              {result.type === 'attachment' && result.metadata.fileSize && (
                <>
                  <span>•</span>
                  <span>{formatFileSize(result.metadata.fileSize)}</span>
                </>
              )}

              {/* 活動類型 */}
              {result.metadata.activityType && (
                <>
                  <span>•</span>
                  <span>
                    {result.metadata.activityType === 'CREATED' && '建立'}
                    {result.metadata.activityType === 'UPDATED' && '更新'}
                    {result.metadata.activityType === 'STATUS_CHANGED' && '狀態變更'}
                    {result.metadata.activityType === 'ASSIGNED' && '指派'}
                    {result.metadata.activityType === 'COMMENTED' && '評論'}
                  </span>
                </>
              )}
              
              {/* 工作日誌相關資訊 */}
              {result.type === 'worklog' && (
                <>
                  {result.metadata.workLogStatus && (
                    <>
                      <span>•</span>
                      <span className={`text-xs font-medium ${
                        result.metadata.workLogStatus === 'PUBLISHED' ? 'text-green-600' :
                        result.metadata.workLogStatus === 'DRAFT' ? 'text-gray-600' :
                        'text-yellow-600'
                      }`}>
                        {result.metadata.workLogStatus === 'PUBLISHED' ? '已發布' :
                         result.metadata.workLogStatus === 'DRAFT' ? '草稿' :
                         '已封存'}
                      </span>
                    </>
                  )}
                  {result.metadata.workLogDateRange && (
                    <>
                      <span>•</span>
                      <span className="text-xs text-gray-600">
                        {result.metadata.workLogDateRange}
                      </span>
                    </>
                  )}
                </>
              )}
              
              {/* 議題狀態（回收桶/封存）*/}
              {(result.metadata.issueDeleted || result.metadata.issueArchived) && (
                <>
                  <span>•</span>
                  {result.metadata.issueDeleted && (
                    <span className="text-red-600 font-medium">在回收桶</span>
                  )}
                  {result.metadata.issueArchived && (
                    <span className="text-orange-600 font-medium">已封存</span>
                  )}
                </>
              )}
            </div>

            </div>
          )
        })}
      </div>

      {/* 分頁控制 */}
      {pagination.totalCount > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              顯示第 {(pagination.page - 1) * pagination.limit + 1} 到{' '}
              {Math.min(pagination.page * pagination.limit, pagination.totalCount)} 項，
              共 {pagination.totalCount} 項結果
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">每頁顯示：</span>
              <select 
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">筆</span>
            </div>
          </div>
          
          {pagination.totalPages > 1 && (
            <div className="flex space-x-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一頁
              </button>
              
              {/* 頁碼按鈕 */}
              {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                const pageNum = i + 1
                const isCurrentPage = pageNum === pagination.page
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`px-3 py-2 text-sm border rounded-md ${
                      isCurrentPage
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一頁
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchResults
